import type {
  LicenseSkillAssignment,
  Skill,
  SkillCategory,
  SkillExample,
} from '@/types/domain';

type UnknownRecord = Record<string, unknown>;

const SKILL_CATEGORIES: SkillCategory[] = [
  'file-processing',
  'data-extraction',
  'utilities',
  'custom',
];

const isRecord = (value: unknown): value is UnknownRecord =>
  typeof value === 'object' && value !== null;

const asString = (value: unknown, fallback = ''): string =>
  typeof value === 'string' ? value : fallback;

const asNumber = (value: unknown, fallback = 0): number =>
  typeof value === 'number' && Number.isFinite(value) ? value : fallback;

const asBoolean = (value: unknown, fallback = false): boolean =>
  typeof value === 'boolean' ? value : fallback;

const asStringArray = (value: unknown): string[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter((item): item is string => typeof item === 'string');
};

const asSkillExamples = (value: unknown): SkillExample[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .filter(isRecord)
    .map((item) => ({
      input: asString(item.input),
      output: asString(item.output),
      ...(typeof item.description === 'string' ? { description: item.description } : {}),
    }));
};

const asSkillCategory = (value: unknown): SkillCategory => {
  if (typeof value !== 'string') {
    return 'custom';
  }

  return SKILL_CATEGORIES.includes(value as SkillCategory)
    ? (value as SkillCategory)
    : 'custom';
};

const readDataContainer = (payload: unknown): unknown => {
  if (!isRecord(payload)) {
    return payload;
  }

  if ('data' in payload) {
    return payload.data;
  }

  return payload;
};

const toArray = (payload: unknown): unknown[] => {
  if (Array.isArray(payload)) {
    return payload;
  }
  return [];
};

export const mapSkillFromApi = (payload: unknown): Skill => {
  const item = isRecord(payload) ? payload : {};
  const createdAt = asString(item.created_at, asString(item.createdAt));
  const updatedAt = asString(item.updated_at, asString(item.updatedAt));
  const author = typeof item.author === 'string' ? item.author : null;
  const builtin = typeof item.builtin === 'boolean' ? item.builtin : null;
  const example = typeof item.example === 'string' ? item.example : null;

  return {
    id: asString(item.id),
    name: asString(item.name),
    description: asString(item.description),
    category: asSkillCategory(item.category),
    version: asString(item.version, '1.0.0'),
    ...(author ? { author } : {}),
    tags: asStringArray(item.tags),
    capabilities: asStringArray(item.capabilities),
    is_active: asBoolean(item.is_active, asBoolean(item.isActive)),
    priority: asNumber(item.priority, 50),
    ...(builtin !== null ? { builtin } : {}),
    ...(example ? { example } : {}),
    instructions: asString(item.instructions),
    examples: asSkillExamples(item.examples),
    tools: asStringArray(item.tools),
    dependencies: asStringArray(item.dependencies),
    created_at: createdAt,
    updated_at: updatedAt,
  };
};

export const mapLicenseSkillAssignmentFromApi = (
  payload: unknown
): LicenseSkillAssignment => {
  const item = isRecord(payload) ? payload : {};

  return {
    id: asString(item.id),
    name: asString(item.name),
    category: asString(item.category),
    enabled: asBoolean(item.enabled, asBoolean(item.is_active)),
  };
};

export const mapSkillListPayload = (payload: unknown): Skill[] => {
  const dataContainer = readDataContainer(payload);
  const source = isRecord(dataContainer) && Array.isArray(dataContainer.skills)
    ? dataContainer.skills
    : dataContainer;

  return toArray(source).map(mapSkillFromApi);
};

export const mapLicenseSkillListPayload = (
  payload: unknown
): LicenseSkillAssignment[] => {
  const dataContainer = readDataContainer(payload);
  return toArray(dataContainer).map(mapLicenseSkillAssignmentFromApi);
};
